/**
 * ExpensesListPage — admin/owner Expenses list (Prompt 11E.8 §6).
 *
 * Uses the shared responsive `<Table>` (overflow-x scroll on desktop,
 * stacked cards below 768px) rather than a raw `<table>` — the reports-tab
 * overlap fix (task #22) is a lesson this new table follows from day one
 * instead of repeating.
 *
 * data-testid: candeza-expenses-list-page
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { PageContainer } from '@/components/layout';
import { Button, Table, Dropdown, Modal } from '@/components/common';
import { formatAmount } from '@/api/dashboard';
import {
  useExpenses,
  useVoidExpense,
  EXPENSE_CATEGORY_OPTIONS,
  type ExpenseResponse,
  type ExpenseCategory,
} from '@/features/expenses/api/expenses';
import { RecordExpenseModal } from '@/features/expenses/components/RecordExpenseModal';

const now = new Date();

const CATEGORY_LABELS: Record<ExpenseCategory, string> = Object.fromEntries(
  EXPENSE_CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
) as Record<ExpenseCategory, string>;

const VoidExpenseModal: React.FC<{ expense: ExpenseResponse; onClose: () => void }> = ({
  expense,
  onClose,
}) => {
  const [reason, setReason] = useState('');
  const voidMut = useVoidExpense();

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      await voidMut.mutateAsync({ id: expense.id, reason: reason.trim() });
      toast.success('Expense voided');
      onClose();
    } catch {
      // client toasts the failure
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Void expense — ${formatAmount(expense.amount)}`} size="sm">
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }} htmlFor="void-expense-reason">
        Reason *
      </label>
      <textarea
        id="void-expense-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        style={{ width: '100%', minHeight: 80, padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.4)' }}
        data-testid="candeza-expenses-void-reason"
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="ghost" onClick={onClose} disabled={voidMut.isPending}>Keep expense</Button>
        <Button variant="danger" onClick={submit} isLoading={voidMut.isPending} data-testid="candeza-expenses-void-confirm">
          Void expense
        </Button>
      </div>
    </Modal>
  );
};

export const ExpensesListPage: React.FC = () => {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [voidTarget, setVoidTarget] = useState<ExpenseResponse | null>(null);

  const { data, isLoading } = useExpenses({ year, month });

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: new Date(2000, i, 1).toLocaleString('en-IN', { month: 'long' }),
  }));
  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const y = now.getFullYear() - i;
    return { value: String(y), label: String(y) };
  });

  return (
    <PageContainer
      title="Expenses"
      subtitle="Office operational expense log — out of the student billing ledger"
      data-testid="candeza-expenses-list-page"
      actions={
        <Button variant="primary" onClick={() => setShowRecordModal(true)} data-testid="candeza-expenses-add-btn">
          Record Expense
        </Button>
      }
    >
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <Dropdown
          value={String(month)}
          onChange={(v) => setMonth(Number(v))}
          options={monthOptions}
          testId="candeza-expenses-month-filter"
        />
        <Dropdown
          value={String(year)}
          onChange={(v) => setYear(Number(v))}
          options={yearOptions}
          testId="candeza-expenses-year-filter"
        />
      </div>

      {!isLoading && data && (
        <div style={{ marginBottom: 12, fontWeight: 600 }} data-testid="candeza-expenses-total">
          Total: {formatAmount(data.total_amount)} across {data.total} expense{data.total === 1 ? '' : 's'}
        </div>
      )}

      <Table<ExpenseResponse>
        data={data?.items ?? []}
        isLoading={isLoading}
        testId="candeza-expenses-table"
        getRowTestId={(e) => `candeza-expenses-row-${e.id}`}
        emptyMessage="No expenses recorded for this month."
        columns={[
          { key: 'date', header: 'Date', isPrimary: true },
          {
            key: 'category',
            header: 'Category',
            render: (e) => CATEGORY_LABELS[e.category],
          },
          { key: 'vendor', header: 'Vendor', render: (e) => e.vendor ?? '—' },
          {
            key: 'amount',
            header: 'Amount',
            render: (e) => formatAmount(e.amount),
          },
          {
            key: 'status',
            header: 'Status',
            render: (e) => (e.status === 'void' ? 'Void' : 'Active'),
          },
          {
            key: 'actions',
            header: '',
            render: (e) =>
              e.status === 'active' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setVoidTarget(e)}
                  data-testid={`candeza-expenses-void-btn-${e.id}`}
                >
                  Void
                </Button>
              ) : (
                <span style={{ color: '#94a3b8', fontSize: 12 }}>{e.void_reason}</span>
              ),
          },
        ]}
      />

      {showRecordModal && <RecordExpenseModal onClose={() => setShowRecordModal(false)} />}
      {voidTarget && <VoidExpenseModal expense={voidTarget} onClose={() => setVoidTarget(null)} />}
    </PageContainer>
  );
};
